import { addDays, differenceInCalendarDays, format, parseISO } from 'date-fns';
import {
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CloudUpload,
  NotebookPen,
  Warehouse,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import {
  useBeforeUnload,
  useBlocker,
  useLocation,
  useParams,
  useSearchParams,
  type Location,
} from 'react-router-dom';
import { AppHeaderContent } from '@/components/AppHeaderContent';
import { ModalOverlay } from '@/components/ModalOverlay';
import { useToast } from '@/components/toastStore';
import { extractApiMessage } from '@/lib/api';
import { fmtFecha, fmtInt } from '@/lib/format';
import { useEntityId, usePendingOperations } from '@/lib/offline/operations';
import { useCamada } from '@/lib/queries/camadas';
import { useLotesDeCamada } from '@/lib/queries/lotesNave';
import { useNaves } from '@/lib/queries/naves';
import {
  useParteDelDiaPorCamada,
  useRegistrosDeLote,
  useUpsertPartePorCamada,
} from '@/lib/queries/registrosDiarios';
import type { ParteDelDiaPorCamada, ParteDelDiaUpsertInput } from '@/lib/types';
import { useAuthStore } from '@/stores/auth';
import { BeforePartStats } from './BeforePartStats';

type Draft = { lote: number; muerte: string; triaje: string };
type Viewed = { owner: number | null; camada: number; data: ParteDelDiaPorCamada };
const total = (r: { bajas_muerte: number; bajas_triaje: number }) =>
  r.bajas_muerte + r.bajas_triaje;

/** Un día se registra nave por nave. El formulario conserva la revisión que el usuario leyó. */
export function RegistroDiarioPage() {
  const { camadaId: camadaParam } = useParams<{ camadaId: string }>();
  const camadaId = useEntityId(camadaParam, 'camadas');
  const [params, setParams] = useSearchParams();
  const today = format(new Date(), 'yyyy-MM-dd');
  const fecha = params.get('fecha') ?? today;
  const selectedId = useEntityId(params.get('lote'), 'lotes');
  const owner = useAuthStore((s) => s.user?.id ?? null);
  const role = useAuthStore((s) => s.user?.rol);
  const canManage = role === 'admin' || role === 'ganadero';
  const camadaQ = useCamada(camadaId);
  const camada = camadaQ.data;
  const dateValid =
    /^\d{4}-\d{2}-\d{2}$/.test(fecha) &&
    !Number.isNaN(parseISO(fecha).getTime()) &&
    fecha <= today &&
    !!camada &&
    fecha >= camada.fecha_entrada;
  const parteQ = useParteDelDiaPorCamada(camadaId, dateValid ? fecha : null);
  const lotesQ = useLotesDeCamada(camadaId);
  const navesQ = useNaves();
  const registrosQ = useRegistrosDeLote(selectedId);
  const { operations } = usePendingOperations();
  const [viewed, setViewed] = useState<Viewed>();
  const baseline =
    viewed?.owner === owner && viewed.camada === camadaId && viewed.data.fecha === fecha
      ? viewed.data
      : undefined;
  const [draft, setDraft] = useState<Draft | null>(null);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [justPending, setJustPending] = useState<Set<number>>(new Set());
  const dirty = useRef(false),
    saving = useRef(false);
  const errorRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const context = `${owner}:${camadaId}:${fecha}`;
  const contextRef = useRef(context);
  contextRef.current = context;
  const adopted = useRef<{ context: string; data: ParteDelDiaPorCamada } | null>(null);
  const upsert = useUpsertPartePorCamada(camadaId ?? 0, baseline);
  const toast = useToast(),
    location = useLocation();
  const returnLocation = useRef(
    (location.state as { backgroundLocation?: Location } | null)?.backgroundLocation,
  );
  const source = returnLocation.current
    ? `${returnLocation.current.pathname}${returnLocation.current.search}`
    : '/';
  const records = baseline?.registros ?? [];
  const selected = records.find((r) => r.lote_nave_id === selectedId);
  const activeDraft = draft?.lote === selectedId ? draft : null;
  const notesDirty = !!baseline && notes.trim() !== (baseline.observaciones ?? '').trim();
  const formDirty =
    !!selected &&
    !!activeDraft &&
    (selected.id == null
      ? activeDraft.muerte !== '' || activeDraft.triaje !== ''
      : activeDraft.muerte.trim() !== String(selected.bajas_muerte) ||
        activeDraft.triaje.trim() !== String(selected.bajas_triaje));
  dirty.current = notesDirty || formDirty;
  const blocker = useBlocker(() => dirty.current || saving.current);
  const blockerRef = useRef(blocker);
  blockerRef.current = blocker;
  useBeforeUnload((event) => {
    if (dirty.current || saving.current) {
      event.preventDefault();
      event.returnValue = '';
    }
  });

  useEffect(() => {
    dirty.current = false;
    adopted.current = null;
    setViewed(undefined);
    setDraft(null);
    setNotes('');
    setError(null);
    setJustPending(new Set());
  }, [camadaId, fecha, owner]);
  useEffect(() => {
    if (
      !parteQ.data ||
      parteQ.data.fecha !== fecha ||
      camadaId == null ||
      dirty.current ||
      saving.current
    )
      return;
    if (adopted.current?.context === context && adopted.current.data === parteQ.data) return;
    adopted.current = { context, data: parteQ.data };
    setViewed({ owner, camada: camadaId, data: parteQ.data });
    // Adoptar valores y revisión juntos: nunca emparejar un borrador viejo con una revisión nueva.
    const refreshed = parteQ.data.registros.find((r) => r.lote_nave_id === selectedId);
    if (refreshed)
      setDraft({
        lote: refreshed.lote_nave_id,
        muerte: refreshed.id == null ? '' : String(refreshed.bajas_muerte),
        triaje: refreshed.id == null ? '' : String(refreshed.bajas_triaje),
      });
    setNotes(parteQ.data.observaciones ?? '');
    if (!parteQ.data.local_pending) setJustPending(new Set());
  }, [parteQ.data, fecha, owner, camadaId, context, busy, selectedId]);
  useEffect(() => {
    if (!selected || dirty.current || saving.current) return;
    setDraft({
      lote: selected.lote_nave_id,
      muerte: selected.id == null ? '' : String(selected.bajas_muerte),
      triaje: selected.id == null ? '' : String(selected.bajas_triaje),
    });
    setError(null);
  }, [selected]);
  useEffect(() => {
    headingRef.current?.focus({ preventScroll: true });
  }, [selectedId, fecha]);
  useEffect(() => {
    if (error) errorRef.current?.focus();
  }, [error]);

  function changeView(lote: number | null, day = fecha) {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('fecha', day);
        if (lote == null) next.delete('lote');
        else next.set('lote', String(lote));
        return next;
      },
      { replace: true, state: location.state },
    );
  }
  function field(key: 'muerte' | 'triaje', value: string) {
    if (!selected) return;
    dirty.current = true;
    setDraft((prev) => ({ ...prev!, lote: selected.lote_nave_id, [key]: value }));
    setError(null);
  }
  function nombre(loteId: number) {
    const lote = lotesQ.data?.find((l) => l.id === loteId);
    return (
      navesQ.data?.find((n) => n.id === lote?.nave_id)?.nombre ||
      records.find((r) => r.lote_nave_id === loteId)?.nave_codigo ||
      `Nave ${loteId}`
    );
  }
  const lote = lotesQ.data?.find((l) => l.id === selectedId);
  const pending = new Set(
    operations
      .filter(
        (o) =>
          o.path === `/v1/camadas/${camadaId}/registros-diarios` &&
          (o.body as { fecha?: string }).fecha === fecha,
      )
      .flatMap((o) => (o.body as ParteDelDiaUpsertInput).partes.map((p) => p.lote_nave_id)),
  );
  justPending.forEach((id) => pending.add(id));
  const completed = records.filter((r) => r.id != null).length;
  const allComplete = records.length > 0 && completed === records.length;
  const allKnown = !!baseline;
  const canEdit = canManage && !!baseline && !!camada && !camada.cerrada && dateValid && !busy;
  const day =
    camada && dateValid
      ? differenceInCalendarDays(parseISO(fecha), parseISO(camada.fecha_entrada)) + 1
      : null;
  const calendarAnchor = dateValid ? fecha : today;
  const dates = Array.from({ length: 5 }, (_, i) =>
    format(addDays(parseISO(calendarAnchor), i - 2), 'yyyy-MM-dd'),
  );
  const longDate = dateValid
    ? new Intl.DateTimeFormat('es-ES', { dateStyle: 'long' }).format(parseISO(fecha))
    : 'Elige una fecha válida de la camada.';

  async function save(kind: 'record' | 'notes') {
    if (saving.current || !canEdit || !baseline || camadaId == null) return;
    let body: ParteDelDiaUpsertInput;
    if (kind === 'record') {
      if (!selected || !activeDraft) return;
      if (
        [activeDraft.muerte, activeDraft.triaje].some(
          (v) => v.trim() === '' || !/^\d+$/.test(v) || !Number.isSafeInteger(Number(v)),
        )
      ) {
        setError('Introduce un entero no negativo en Muertes y Triajes, también si es 0.');
        return;
      }
      const muerte = Number(activeDraft.muerte),
        triaje = Number(activeDraft.triaje);
      if (lote && muerte + triaje > lote.num_animales_asignados) {
        setError('Las bajas superan los animales asignados a esta nave.');
        return;
      }
      if (
        selected.id != null &&
        muerte === selected.bajas_muerte &&
        triaje === selected.bajas_triaje
      )
        return;
      body = {
        fecha,
        partes: [
          { lote_nave_id: selected.lote_nave_id, bajas_muerte: muerte, bajas_triaje: triaje },
        ],
        observaciones: baseline.observaciones,
      };
    } else {
      if (!notesDirty) return;
      body = { fecha, partes: [], observaciones: notes.trim() || null };
    }
    saving.current = true;
    setBusy(true);
    setError(null);
    const savedContext = context;
    try {
      const saved = await upsert.mutateAsync(body);
      if (contextRef.current !== savedContext) return;
      setViewed({ owner, camada: camadaId, data: saved });
      setNotes(saved.observaciones ?? '');
      setDraft(null);
      dirty.current = false;
      saving.current = false;
      if (saved.local_pending && kind === 'record')
        setJustPending((prev) => new Set([...prev, selected!.lote_nave_id]));
      if (blockerRef.current.state === 'blocked') blockerRef.current.reset();
      const next =
        kind === 'record' && selected!.id == null
          ? [
              ...saved.registros.slice(records.indexOf(selected!) + 1),
              ...saved.registros.slice(0, records.indexOf(selected!)),
            ].find((r) => r.id == null)
          : undefined;
      toast.show({
        kind: 'success',
        message:
          (kind === 'record' ? 'Parte guardado.' : 'Observaciones guardadas.') +
          (next ? ` · Sigue ${nombre(next.lote_nave_id)}` : ''),
      });
      if (kind === 'record') changeView(next?.lote_nave_id ?? null);
    } catch (err) {
      if (contextRef.current === savedContext) setError(extractApiMessage(err));
    } finally {
      saving.current = false;
      setBusy(false);
    }
  }

  const listUrl = `${location.pathname}?fecha=${fecha}`;
  return (
    <main
      className={`parte-page mx-auto max-w-md px-4 ${selectedId != null ? 'parte-editor' : ''}`}
    >
      <header className="ui-global-header" data-testid="app-header">
        <AppHeaderContent
          backTo={selectedId != null ? listUrl : source}
          backLabel={
            selectedId != null ? 'Listado' : source.startsWith('/naves/') ? 'Nave' : 'Inicio'
          }
        />
      </header>
      <h1 className="ui-page-title" ref={headingRef} tabIndex={-1}>
        Parte diario
      </h1>
      <p className="parte-context">
        <span>{camada?.codigo_lote ?? 'Camada'}</span>
        <span>
          {selectedId != null
            ? fmtFecha(fecha)
            : `${camada?.sexo === 'machos' ? 'Machos' : 'Hembras'}${day ? ` · Día ${day}` : ''}`}
        </span>
      </p>
      {error && (
        <div id="parte-error" className="parte-error" role="alert" ref={errorRef} tabIndex={-1}>
          {error}
        </div>
      )}
      {camadaQ.isLoading || lotesQ.isLoading ? (
        <p role="status">Cargando partes…</p>
      ) : !camada || camadaQ.isError ? (
        <p role="alert">Camada no disponible. Conecta para actualizar.</p>
      ) : !dateValid ? (
        <p role="alert">{longDate}</p>
      ) : camada.cerrada ? (
        <p role="alert">La camada está cerrada. Consulta sus partes desde el historial.</p>
      ) : lotesQ.error && !lotesQ.data ? (
        <p role="alert">Asignaciones no disponibles. Conecta para actualizar.</p>
      ) : (
        <>
          {!canManage && <p className="parte-caption">Tu perfil permite consultar los partes.</p>}
          {selectedId == null && (
            <>
              <section className="parte-calendar" aria-label="Fecha del parte">
                <div className="parte-days">
                  {dates.map((date) => {
                    const disabled = busy || date < camada.fecha_entrada || date > today;
                    return (
                      <button
                        key={date}
                        className="parte-day"
                        aria-label={new Intl.DateTimeFormat('es-ES', { dateStyle: 'full' }).format(
                          parseISO(date),
                        )}
                        aria-pressed={date === fecha}
                        disabled={disabled}
                        onClick={() => changeView(null, date)}
                      >
                        <span>
                          {new Intl.DateTimeFormat('es-ES', { weekday: 'short' })
                            .format(parseISO(date))
                            .replace('.', '')}
                        </span>
                        <strong>{Number(date.slice(-2))}</strong>
                      </button>
                    );
                  })}
                  <label className="parte-date-picker">
                    <CalendarDays size={23} aria-hidden />
                    <span className="sr-only">Elegir otra fecha</span>
                    <input
                      type="date"
                      aria-label="Elegir otra fecha"
                      min={camada.fecha_entrada}
                      max={today}
                      value={dateValid ? fecha : ''}
                      disabled={busy}
                      onChange={(e) => {
                        if (e.target.value) changeView(null, e.target.value);
                      }}
                    />
                  </label>
                </div>
                <p className="parte-caption">{longDate}</p>
              </section>
              {allKnown ? (
                <>
                  <section className="parte-progress" aria-label="Progreso del día">
                    <div>
                      <h2>
                        {allComplete
                          ? 'Partes completos'
                          : canManage
                            ? 'Completa el día'
                            : 'Partes del día'}
                      </h2>
                      <strong>
                        {completed}/{records.length}
                      </strong>
                    </div>
                    <progress max={Math.max(1, records.length)} value={completed} />
                    <p>
                      {pending.size || baseline.local_pending
                        ? 'Hay cambios guardados en el móvil, pendientes de confirmar.'
                        : !canManage
                          ? 'Puedes consultar los partes de cada nave.'
                          : allComplete
                            ? 'Puedes revisar o corregir cualquier parte.'
                            : 'Registra cada nave, también cuando haya cero bajas.'}
                    </p>
                  </section>
                  <ul className="parte-naves">
                    {records.map((r) => {
                      const local = r.id != null && (r.id < 0 || pending.has(r.lote_nave_id));
                      const assignment = lotesQ.data?.find(
                        (l) => l.id === r.lote_nave_id,
                      )?.num_animales_asignados;
                      return (
                        <li key={r.lote_nave_id}>
                          <button
                            className="parte-nave"
                            disabled={busy}
                            onClick={() => changeView(r.lote_nave_id)}
                            aria-label={`${nombre(r.lote_nave_id)}, ${r.id == null ? 'Pendiente' : local ? 'En el móvil' : 'Completado'}`}
                          >
                            <span className="parte-house">
                              <Warehouse size={25} aria-hidden />
                            </span>
                            <span className="parte-nave-name">
                              <strong>{nombre(r.lote_nave_id)}</strong>
                              <small>
                                {assignment == null
                                  ? 'Aves sin actualizar'
                                  : `${fmtInt(assignment)} aves de referencia`}
                              </small>
                            </span>
                            <span className="parte-nave-status">
                              <span>
                                {r.id == null ? (
                                  'Pendiente'
                                ) : local ? (
                                  <>
                                    <CloudUpload size={15} aria-hidden />
                                    En el móvil
                                  </>
                                ) : (
                                  <>
                                    <CheckCircle2 size={15} aria-hidden />
                                    Completado
                                  </>
                                )}
                              </span>
                              <small>
                                {r.id == null ? 'Sin registrar' : `${fmtInt(total(r))} bajas`}
                              </small>
                            </span>
                            <ChevronRight size={15} aria-hidden />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                  {records.length === 0 && (
                    <p className="parte-caption">
                      Asigna animales a las naves antes de registrar partes.
                    </p>
                  )}
                  <details className="parte-disclosure">
                    <summary>
                      <NotebookPen size={18} aria-hidden />
                      Observaciones del día{' '}
                      <span>{baseline.observaciones ? 'Registradas' : 'Opcional'}</span>
                      <ChevronDown size={16} aria-hidden />
                    </summary>
                    <label className="parte-caption" htmlFor="parte-notes">
                      Observaciones comunes
                    </label>
                    <textarea
                      className="ui-field w-full"
                      id="parte-notes"
                      rows={3}
                      maxLength={2000}
                      value={notes}
                      disabled={!canEdit}
                      onChange={(e) => {
                        dirty.current = true;
                        setNotes(e.target.value);
                        setError(null);
                      }}
                    />
                    <button
                      className="ui-primary w-full"
                      disabled={!canEdit || !notesDirty || !records.length}
                      onClick={() => void save('notes')}
                    >
                      {busy ? 'Guardando…' : 'Guardar observaciones'}
                    </button>
                  </details>
                </>
              ) : (
                <p role={parteQ.error ? 'alert' : 'status'}>
                  {parteQ.error
                    ? 'Partes no disponibles. Conecta para actualizar.'
                    : 'Cargando el día…'}
                </p>
              )}
            </>
          )}
          {selectedId != null &&
            (selected ? (
              <>
                <section className="parte-editor-nave">
                  <span className="parte-house">
                    <Warehouse size={27} aria-hidden />
                  </span>
                  <div>
                    <h2>{nombre(selectedId)}</h2>
                    <p>
                      {lote
                        ? `${fmtInt(lote.num_animales_asignados)} aves de referencia`
                        : 'Aves sin actualizar'}
                    </p>
                  </div>
                  <span className="parte-caption">
                    {selected.id == null
                      ? 'Pendiente'
                      : canManage
                        ? 'Editar parte'
                        : 'Consultar parte'}
                  </span>
                </section>
                <form
                  id="parte-record-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void save('record');
                  }}
                  noValidate
                >
                  <section className="parte-inputs">
                    <h2>Bajas del día</h2>
                    {canManage && <p className="parte-caption">Introduce 0 si no hubo bajas.</p>}
                    <div className="parte-two-fields">
                      {[
                        { key: 'muerte' as const, label: 'Muertes', hint: 'next' as const },
                        { key: 'triaje' as const, label: 'Triajes', hint: 'done' as const },
                      ].map(({ key, label, hint }) => (
                        <div key={key}>
                          <label htmlFor={`parte-${key}`}>{label}</label>
                          <input
                            className="ui-field"
                            id={`parte-${key}`}
                            type="number"
                            inputMode="numeric"
                            enterKeyHint={hint}
                            required
                            min={0}
                            step={1}
                            value={activeDraft?.[key] ?? ''}
                            aria-invalid={!!error}
                            aria-describedby={error ? 'parte-error' : undefined}
                            disabled={!canEdit}
                            onChange={(e) => field(key, e.target.value)}
                          />
                        </div>
                      ))}
                    </div>
                    <div className="parte-live-total">
                      <span>Total de bajas</span>
                      <strong>
                        {activeDraft &&
                        /^\d+$/.test(activeDraft.muerte) &&
                        /^\d+$/.test(activeDraft.triaje)
                          ? fmtInt(Number(activeDraft.muerte) + Number(activeDraft.triaje))
                          : '—'}{' '}
                        bajas
                      </strong>
                    </div>
                  </section>
                </form>
                <BeforePartStats
                  registros={registrosQ.data}
                  fecha={fecha}
                  entrada={camada.fecha_entrada}
                  aves={lote?.num_animales_asignados ?? null}
                />
                <p className="parte-caption">
                  Las observaciones del día se revisan desde el listado.
                </p>
                <div className="parte-editor-actions">
                  <button className="ui-secondary" disabled={busy} onClick={() => changeView(null)}>
                    Volver al listado
                  </button>
                  <button
                    className="ui-primary"
                    form="parte-record-form"
                    type="submit"
                    data-testid="registro-submit"
                    disabled={!canEdit || !activeDraft || (selected.id != null && !formDirty)}
                  >
                    {busy
                      ? 'Guardando…'
                      : selected.id != null
                        ? 'Guardar cambios'
                        : records.some((r) => r.id == null && r.lote_nave_id !== selectedId)
                          ? 'Guardar y siguiente'
                          : 'Guardar parte'}
                    <ChevronRight size={18} aria-hidden />
                  </button>
                </div>
              </>
            ) : (
              <p role={parteQ.error ? 'alert' : 'status'}>
                {baseline
                  ? 'Esta nave no pertenece a la camada.'
                  : parteQ.error
                    ? 'Parte no disponible. Conecta para actualizar.'
                    : 'Cargando parte…'}
              </p>
            ))}
        </>
      )}
      {blocker.state === 'blocked' && (
        <ModalOverlay
          ariaLabel="Cambios sin guardar"
          placement="bottom"
          className="max-w-md"
          onBackdrop={() => blocker.reset()}
        >
          <h2 className="ui-page-title">{busy ? 'Guardando parte…' : 'Cambios sin guardar'}</h2>
          <p className="py-3">
            {busy
              ? 'Espera a que termine el guardado antes de salir.'
              : 'Puedes seguir editando o salir sin guardar este borrador.'}
          </p>
          <button className="ui-primary w-full" onClick={() => blocker.reset()}>
            Seguir editando
          </button>
          {!busy && (
            <button
              className="ui-secondary mt-2 w-full"
              onClick={() => {
                dirty.current = false;
                setDraft(null);
                setNotes(baseline?.observaciones ?? '');
                blocker.proceed();
              }}
            >
              Salir sin guardar
            </button>
          )}
        </ModalOverlay>
      )}
    </main>
  );
}
export default RegistroDiarioPage;
